import AvatarWithName from "v2/common/AvatarWithName";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import { Avatar } from "primereact/avatar";
import ServiceStackIcon from "app/assets/IconComponent/ServiceStackIcon";

interface PolicyRow {
  id: string;
  insuredName: string;
  insuredAvatar?: string;
  policyNumber: string;
  accountValue: string;
  surrenderValue: string;
  benefit: string;
  ownerName: string;
  ownerLogo?: string;
  beneficiary: string;
  policyIcon: string;
  primaryColor?: string;
  primaryColorDark?: string;
  insuranceCompanyLogo?: string;
}

const COLUMNS: { key: keyof PolicyRow; label: string; width: string }[] = [
  { key: "insuredName", label: "Insured", width: "18%" },
  { key: "policyNumber", label: "Policy #", width: "15%" },
  { key: "accountValue", label: "Account Value", width: "13%" },
  { key: "surrenderValue", label: "Surrender Value", width: "13%" },
  { key: "benefit", label: "Benefit", width: "10%" },
  { key: "ownerName", label: "Owner", width: "18%" },
  { key: "beneficiary", label: "Beneficiary", width: "13%" },
];

export type { PolicyRow };

interface PolicyInformationCardProps {
  policies?: PolicyRow[];
}

const PolicyInformationCard = ({ policies }: PolicyInformationCardProps = {}) => {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const rows = policies ?? [];

  const headerText = isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]";
  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const thBg = isDark ? "bg-[#1E1E2E]" : "bg-white";
  const thText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const tdText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const divider = isDark ? "border-[#2E2E42]" : "border-[#F1F5F9]";

  const PolicyBadge = ({ policyIcon, color }: { policyIcon?: string; color: string }) => (
    <div
      className="w-max rounded-full p-1 flex items-center justify-center"
      style={{ border: `2px solid ${color}` }}
    >
      <div className="w-full h-full rounded-full shadow-sm flex items-center justify-center">
        <Avatar image={policyIcon} shape="circle" style={{ width: "32", height: "32" }} />
      </div>
    </div>
  );

  const renderCell = (row: PolicyRow, col: (typeof COLUMNS)[number]) => {
    const brandColor = (isDark ? row.primaryColorDark : row.primaryColor) ?? "#22C55E";

    switch (col.key) {
      case "insuredName":
        return (
          <AvatarWithName
            imageUrl={row.insuredAvatar}
            name={row.insuredName}
            tooltipText={row.insuredName.length > 20 ? row.insuredName : undefined}
            avatarStyle={{ border: "1px solid #cbd5e1" }}
            nameClassName="font-medium text-[14px]"
          />
        );

      case "policyNumber":
        return (
          <div className="flex items-center gap-2">
            <PolicyBadge policyIcon={row?.insuranceCompanyLogo} color={brandColor} />
            <div className="flex items-center gap-1">
              <span className="w-[12px] h-[12px] rounded-full bg-[#22C55E] flex-shrink-1" />
              <span className={`text-[14px] font-semibold ${tdText}`}>{row.policyNumber}</span>
            </div>
          </div>
        );

      case "accountValue":
      case "surrenderValue":
      case "benefit":
        return <span className={`text-[14px] font-bold ${tdText}`}>{row[col.key]}</span>;

      case "ownerName":
        return (
          <AvatarWithName
            imageUrl={row.ownerLogo}
            name={row.ownerName}
            tooltipText={row.ownerName.length > 20 ? row.ownerName : undefined}
            avatarStyle={{ border: "1px solid #cbd5e1" }}
            nameClassName="font-medium text-[14px]"
          />
        );

      default:
        return <span className={`text-[14px] ${tdText}`}>{row[col.key]}</span>;
    }
  };

  return (
    <div className={`w-full rounded-2xl shadow-lg  mt-4 ${cardBg}`}>
      <p
        className={`text-[13px] font-semibold conthrax-font p-4  flex items-center bg-[#f5f5f5] gap-2  ${headerText}`}
      >
        <ServiceStackIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} width="18px" />
        Policy Information
      </p>

      {/* ── Table ── */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="border-b-2 border-[#F5F5F5]">
            <tr className={`${thBg}`}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={`px-4 py-3 text-left text-[14px] font-medium uppercase whitespace-nowrap tracking-wider ${thText} border-b ${divider}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.id}
                className={`transition-colors hover:${isDark ? "bg-[#1E1E2E]" : "bg-[#F8FAFC]"} ${
                  index < rows.length - 1 ? `border-b ${divider}` : ""
                }`}
              >
                {COLUMNS.map((col) => (
                  <td key={col.key} className="px-4 py-4">
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PolicyInformationCard;
